import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { DebitRowItem, type DebitRow, type Bucket } from '@/components/RecentDebitsBell';

interface WalletItem {
  id: string;
  name: string;
  active: boolean;
}
interface DailyBalance {
  date: string;
  openingBalance: string | null;
  closingBalance: string | null;
}

const today = (): string => new Date().toISOString().slice(0, 10);

/**
 * Full-page view of recent card / bank debits — three tabs (Untagged /
 * Tagged / Unclaimed) with a free-text filter, for every role. Accounts
 * / admin also get a per-wallet opening / closing balance strip on top.
 */
export function RecentDebitsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAccountant = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';
  const [tab, setTab] = useState<Bucket>('TAGGED');
  const [q, setQ] = useState('');

  const list = useQuery<DebitRow[]>({
    queryKey: ['debits-recent'],
    queryFn: () => api('/api/transactions/recent-debits'),
    refetchInterval: 30_000,
  });
  const items = list.data ?? [];
  const onChanged = () => qc.invalidateQueries({ queryKey: ['debits-recent'] });

  const term = q.trim().toLowerCase();
  const filtered = items
    .filter((d) => d.bucket === tab)
    .filter((d) => {
      if (!term) return true;
      return [d.vendor, d.smsVendor, d.beneficiaryName, d.clientName, d.amount, d.cardNickname, d.bankNickname]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  const tabCount = (b: Bucket) => items.filter((i) => i.bucket === b).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Recent debits</h1>
        <p className="text-sm text-muted-foreground">
          Every card &amp; bank debit — tag each to a client / trip / head.
        </p>
      </div>

      {isAccountant ? <WalletBalanceStrip /> : null}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            {(['TAGGED', 'UNCLAIMED', 'UPI'] as const).map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setTab(b)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                  tab === b ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
                )}
              >
                {b === 'TAGGED' ? 'Tagged' : b === 'UNCLAIMED' ? 'Unclaimed' : 'UPI'} ({tabCount(b)})
              </button>
            ))}
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by vendor, client, amount…"
              className="ml-auto h-8 w-64 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground">
              {term ? 'No debits match that filter.' : 'Nothing here.'}
            </p>
          ) : (
            <ul className="divide-y">
              {filtered.map((d) => (
                <DebitRowItem key={d.id} debit={d} meId={user?.id ?? ''} onChanged={onChanged} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WalletBalanceStrip() {
  const wallets = useQuery<WalletItem[]>({
    queryKey: ['wallets'],
    queryFn: () => api('/api/wallets'),
  });
  const active = (wallets.data ?? []).filter((w) => w.active);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Wallet balances — today</CardTitle>
        <CardDescription>Opening &amp; closing balance per B2B wallet.</CardDescription>
      </CardHeader>
      <CardContent>
        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground">No wallets.</p>
        ) : (
          <div className="space-y-1.5">
            {active.map((w) => (
              <WalletBalanceRow key={w.id} wallet={w} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WalletBalanceRow({ wallet }: { wallet: WalletItem }) {
  const qc = useQueryClient();
  const balances = useQuery<DailyBalance[]>({
    queryKey: ['wallet-balances', wallet.id],
    queryFn: () => api(`/api/wallets/${wallet.id}/balances`),
  });
  const td = today();
  const todayRow = (balances.data ?? []).find((b) => String(b.date).slice(0, 10) === td);
  const [opening, setOpening] = useState('');
  const [closing, setClosing] = useState('');
  const [busy, setBusy] = useState<'opening' | 'closing' | null>(null);

  const openingVal = todayRow?.openingBalance ?? null;
  const closingVal = todayRow?.closingBalance ?? null;

  const save = async (kind: 'opening' | 'closing', value: string) => {
    if (!/^-?\d+(\.\d{1,2})?$/.test(value)) return;
    setBusy(kind);
    try {
      await api(`/api/wallets/${wallet.id}/balances/${kind}`, {
        method: 'POST',
        body: { date: td, balance: value },
      });
      qc.invalidateQueries({ queryKey: ['wallet-balances', wallet.id] });
      if (kind === 'opening') setOpening('');
      else setClosing('');
    } finally {
      setBusy(null);
    }
  };

  const slot = (kind: 'opening' | 'closing', recorded: string | null, val: string, setVal: (v: string) => void) => (
    <div className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-wide text-zinc-500">
        {kind === 'opening' ? 'Open' : 'Close'}
      </span>
      {recorded != null ? (
        <span className="font-mono text-sm font-semibold tabular-nums">₹{recorded}</span>
      ) : (
        <>
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="—"
            className="h-7 w-24 rounded border border-input bg-background px-1.5 text-xs"
          />
          <button
            type="button"
            disabled={busy === kind || !val}
            onClick={() => save(kind, val)}
            className="rounded bg-zinc-900 px-2 py-1 text-[10px] font-semibold text-white disabled:opacity-50"
          >
            Set
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 px-3 py-2">
      <span className="w-44 shrink-0 truncate text-sm font-semibold">{wallet.name}</span>
      {slot('opening', openingVal, opening, setOpening)}
      {slot('closing', closingVal, closing, setClosing)}
    </div>
  );
}
